<template>
    <div class="map-floor-selector" v-if="map && map.floors && map.floors.length > 1">
        <template v-if="!isMobileLayoutOrPreview">
            <b-btn v-for="floor in map.floors" :key="`floor-${floor.id}`" @click="selectFloor(floor)"
                   :variant="floor.id === floorSelected.id ? 'accent': 'white'" class="btn-floor"
                   :class="[{ 'selected': floor.id === floorSelected.id, 'disabled': isDisabled(floor), 'floor-basement': floor.level < 0 }]">
                {{floor.getName()}}

                <span v-if="getFloorClass(floor)" :class="getFloorClass(floor)"/>
            </b-btn>
        </template>

        <b-btn v-else variant="white" no-caret @click="modalVisible = true">
            {{floorSelected.getName()}}
            <i :class="[fontAwesomePrefix, 'fa-angles-up-down']" />
        </b-btn>

        <e-action-sheet v-model="modalVisible">
            <e-action-sheet-item v-for="floor in map.floors" :key="`floor-${floor.id}`" @click="selectFloor(floor)"
                             :link-class="{ 'active': floor.id === floorSelected.id }">
                <i :class="[fontAwesomePrefix, mobileFloorIcon(floor)]"/>
                <span>{{floor.getName()}}</span>
            </e-action-sheet-item>
            <template v-if="showAltitudeCalibration">
                <hr>
                <e-action-sheet-item @click="calibrateAltitude" >
                    <i :class="[fontAwesomePrefix]" class="fa-ruler"/>
                    <span>{{ $t('map.altitudeCalibration.action') }}</span>
                </e-action-sheet-item>
            </template>
        </e-action-sheet>

        <div class="mt-1" style="opacity: 0">
            <b-btn v-if="!isLive()" variant="white" @click="showDebugMenu" pill size="md"
                   class="border ratio-1x1 ml-auto" style="height: 37.92px">
                <i :class="[fontAwesomePrefix, 'fa-code']" />
            </b-btn>
        </div>
    </div>
</template>

<script>
import EActionSheet from "@vue-components/components/e-action-sheet.vue";
import EActionSheetItem from "@vue-components/components/e-action-sheet-item.vue";

export default {
    name: `map-floor-selector`,
    components: {EActionSheetItem, EActionSheet},
    props: {
        map: {type: Object},
        floorSelected: {type: Object},
        navPath: {type: Object},
        currentGpsFloor: {type: Object},
        showAltitudeCalibration: {type: Boolean, default: false},
        splitViewActive: {type: Boolean}
    },
    data() {
        return {
            modalVisible: false
        }
    },
    methods: {
        selectFloor(floor) {
            this.$emit(`selected`, floor);
        },
        isDisabled(floor) {
            return this.navPath && !this.navPath.floors.find(f => f.id === floor.id);
        },
        mobileFloorIcon(floor) {
            let icon = ``;
            if (this.navPath && this.navPath.endFloor && this.navPath.endFloor.id === floor.id) {
                icon = `fa-location-dot`;
            } else if (this.navPath && this.navPath.startFloor && this.navPath.startFloor.id === floor.id) {
                icon = `fa-circle-dot`;
            } else if (this.currentGpsFloor && this.floorSelected.id === floor.id && this.floorSelected.id !== this.currentGpsFloor.id) {
                icon = `fa-circle`;
            } else if (this.currentGpsFloor && (floor.id === this.currentGpsFloor.id)) {
                icon = `fa-location-crosshairs`;
            }
            return icon;
        },
        getFloorClass(floor) {
            if (!this.navPath)
                return false;

            let direction = ``;
            if (this.navPath.startFloor.id === floor.id) {
                if (this.navPath.floorDirection === `flat` && this.navPath.minFloor.level === this.navPath.maxFloor.level) {
                    return `direction-flat`;
                } else {
                    if (floor.level === this.navPath.minFloor.level) {
                        direction = `up`;
                    } else if (floor.level === this.navPath.maxFloor.level) {
                        direction = `down`;
                    }

                    return `floor-nav-path path-start floor-direction-${direction}`;
                }
            }

            if (this.navPath.endFloor.id === floor.id) {
                if (floor.level === this.navPath.maxFloor.level) {
                    direction = `up`;
                } else if (floor.level === this.navPath.minFloor.level) {
                    direction = `down`;
                }

                return `floor-nav-path path-end floor-direction-${direction}`;
            }

            if (this.navPath.minFloor.id === floor.id)
                return `floor-nav-path floor-min`;

            if (this.navPath.maxFloor.id === floor.id)
                return `floor-nav-path floor-max`;

            if (this.navPath.minFloor.level <= floor.level && floor.level <= this.navPath.maxFloor.level) {
                return `floor-nav-path path-between`;
            } else {
                return ``;
            }
        },
        showDebugMenu() {
            this.$emit(`show-dev-menu`);
        },
        calibrateAltitude() {
            this.$emit(`calibrate-altitude`);
        }
    }
}
</script>

<style lang="scss" scoped>
.map-floor-selector {
    position: absolute;
    z-index: 1;
    top: 16px;
    right: 10px;
    text-align: right;
    display: grid;

    --floor-btn-height: 40px;
    --floor-btn-margin: 5px;
    --floor-path-width: 8px;
    --floor-path-height: calc(var(--floor-btn-height) + 2 * var(--floor-btn-margin));
    --floor-half-height: calc(var(--floor-btn-height) / 2 + var(--floor-btn-margin) + var(--floor-path-width));
    --floor-path-arrow-width: 12px;

    .btn-floor {
        display: block;
        margin-bottom: var(--floor-btn-margin);
        position: relative;
    }

    // btn and dropdown button
    .btn, ::v-deep .btn {
        &.disabled {
            opacity: 1;
            color: rgba(black, 0.25) !important;
        }

        //border: 1px solid rgba(black, 0.1);
        box-shadow: 0 3px 8px rgba(black, 0.2);
    }

    .direction-flat {
        position: absolute;
        left: 0;
        right: 0;
        top: 0;
        --floor-path-arrow-width: 8px;

        &:before,
        &:after {
            content: "";
            position: absolute;
            top: calc(var(--floor-btn-height) / 2 - var(--floor-path-arrow-width) - 5px);
            border: var(--floor-path-arrow-width) solid transparent;
        }

        &:before {
            left: calc(-1 * var(--floor-path-arrow-width) - var(--floor-btn-margin));
            border-left-color: var(--map-primary-color);
        }

        &:after {
            left: calc(100% - var(--floor-path-arrow-width) + var(--floor-btn-margin));
            border-right-color: var(--map-primary-color);
        }
    }

    // horizontal line
    .floor-nav-path {
        position: absolute;
        width: 15px;
        height: 8px;
        right: calc(100% + 10px);
        top: 14px;
        display: block;

        &:not(.path-between) {
            background: var(--map-primary-color);
        }

        // Before is vertical line
        &:before {
            content: "";
            position: absolute;
            height: var(--floor-path-height);
            right: 15px;
            background: var(--map-primary-color);
            width: var(--floor-path-width);
            top: -20px;
        }

        &.path-start.floor-direction-up:before,
        &.path-end.floor-direction-down:before {
            height: var(--floor-half-height);
            top: calc(-1 * var(--floor-half-height) + var(--floor-path-width));
        }

        &.path-start.floor-direction-down:before,
        &.path-end.floor-direction-up:before {
            height: var(--floor-half-height);
            top: 0;
        }

        // After are for arrows shapes
        &.floor-nav-path:after {
            content: "";
            position: absolute;
            right: -5px;
            top: -8px;
        }

        // Min & Max Floors are only showing when there is more than 2 floors in the path
        &.floor-nav-path.floor-max,
        &.floor-nav-path.floor-min {
            background: transparent;
        }

        &.floor-nav-path.floor-min:before {
            height: calc(var(--floor-path-height) / 2);
            top: -20px;
        }
        &.floor-nav-path.floor-max:before {
            height: calc(var(--floor-path-height) / 2);
            top: 0;
        }

        // After are for rounded shapes
        &.floor-nav-path.floor-max:after,
        &.floor-nav-path.floor-min:after {
            content: "";
            position: absolute;
            display: block;
            right: 9px;
            top: -6px;
            width: 20px;
            height: 20px;
            border-radius: 50%;
            background: var(--map-primary-color);
        }

        &.path-start:not(.floor-direction-flat):after {
            border: var(--floor-path-arrow-width) solid transparent;
            border-right-color: var(--map-primary-color);
        }

        &.path-end:after {
            border: var(--floor-path-arrow-width) solid transparent;
            border-left-color: var(--map-primary-color);
            margin-right: calc(-1 * var(--floor-path-arrow-width));
        }
    }
}

.layout-mobile {
    .map-floor-selector {
        top: var(--map-top-padding);
        right: 10px;

        :deep(.btn) {
            border: 1px solid var(--border-color);
            border-radius: 999px;
            padding: 8px;
            box-shadow: 0 2px 6px rgba(0,0,0,0.06), 0 10px 30px rgba(0,0,0,0.12);
            background: rgba(255, 255, 255, 0.96);
            -webkit-backdrop-filter: blur(20px) saturate(140%);
            backdrop-filter: blur(20px) saturate(140%);
            color: var(--color-dark) !important;
        }
    }
}

.layout-kiosk {
    .map-floor-selector {
        left: auto;
        top: auto;
        right: 20px;
        bottom: 40px;
    }
}
</style>
